import EngineCleanupBase from './EngineCleanupBase';
import logService from '../wss/logService3';
import userTopicService from '../UserTopicService';
import responseHelper from '../ResponseHelper';
import componentNotifier from '../wss/ComponentNotifier';

import nodeContainer from './YourNoteNodeContainer';

const _name = 'YourNoteEngine';
const _target = 'YourNotes';

class YourNoteEngine extends EngineCleanupBase {
    _activeTopicGroup = null;

    bootup = () => {
        try {
            this.initialize(_name);
        } catch (error) {
            logService.logError(`${_name}.bootup: ${error.message}`);
        }
    };

    /**
     * Called when user selects a group.
     * Clears container and fetches entries for the selected group from BE.
     */
    onGroupSelected = async (group, jwt) => {
        try {
            this._activeTopicGroup = group;
            nodeContainer.clear();
            componentNotifier.notify('LoadingStarted', _target);

            const resp = await userTopicService.getGroupEntries(group, jwt);
            if (responseHelper.check(resp, 'onGroupSelected', _name) < 0) {
                this.onError('Failed to load entries for group');
                componentNotifier.notify('Repaint', _target);
                return;
            }

            const nodes = resp.data?.nodes ?? {};
            Object.keys(nodes).forEach((nodeKey) => {
                const node = nodes[nodeKey];
                nodeContainer.setNode(nodeKey, node.entries ?? [], node.total ?? 0, node.page ?? 1);
            });

            componentNotifier.notify('Repaint', _target);
        } catch (error) {
            logService.logError(`${_name}.onGroupSelected: ${error.message}`);
            componentNotifier.notify('Repaint', _target);
            this.onError(error.message);
        }
    };

    /**
     * Loads a specific page for a node — called from YourNotesNode pager.
     */
    loadPage = async (nodeKey, page, jwt) => {
        try {
            const resp = await userTopicService.getNodePage(this._activeTopicGroup, nodeKey, page, jwt);
            if (responseHelper.check(resp, 'loadPage', _name) < 0) return;
            nodeContainer.setNode(nodeKey, resp.data?.entries ?? [], resp.data?.total ?? 0, page);
            componentNotifier.notify('Repaint', _target);
        } catch (error) {
            logService.logError(`${_name}.loadPage: ${error.message}`);
        }
    };

    /**
     * Updates or adds entries across all nodes.
     * Called from OnJournalUpdatedHandler.
     * Returns true if mutated.
     */
    onJournalUpdated = (data) => {
        try {
            return nodeContainer.addOrUpdate(data);
        } catch (error) {
            logService.logError(`${_name}.addOrUpdate: ${error.message}`);
            return false;
        }
    };

    /**
     * Removes entry from all nodes.
     * Called from OnJournalDeletedHandler.
     * Returns true if mutated.
     */
    deleteEntry = (rk) => {
        try {
            return nodeContainer.deleteEntry(rk);
        } catch (error) {
            logService.logError(`${_name}.deleteEntry: ${error.message}`);
            return false;
        }
    };

    getNodes = () => {
        return nodeContainer.getNodes();
    };

    getEntries = () => {
        const nodes = nodeContainer.getNodes();
        return Object.values(nodes).flatMap((n) => n.entries ?? []);
    };

    getActiveTopicGroup = () => {
        return this._activeTopicGroup;
    };

    clear = () => {
        nodeContainer.clear();
        super.clear();
    };
}

const yourNoteEngine = new YourNoteEngine();
export default yourNoteEngine;
